import * as T from 'three';

// shared the fallback render for characters and maps
export function renderSceneStill(
  scene: T.Scene,
  camera: T.Camera,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
) {
  const context = canvas.getContext('2d');
  if (!context || !width || !height) return;
  const ratio = Math.min(globalThis.devicePixelRatio || 1, 1.5);
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  const pixels = context.createImageData(canvas.width, canvas.height);
  const depth = new Float32Array(canvas.width * canvas.height).fill(Infinity);
  scene.updateMatrixWorld(true);
  camera.updateMatrixWorld(true);
  const cameraPosition = camera.getWorldPosition(new T.Vector3());
  const sun = new T.Vector3(-0.5, 1, 1).normalize();
  const rgb = new T.Color();

  // keep the scene background behind every shape
  if (scene.background instanceof T.Color) {
    scene.background.getRGB(rgb, T.SRGBColorSpace);
    for (let i = 0; i < pixels.data.length; i += 4) {
      pixels.data[i] = rgb.r * 255;
      pixels.data[i + 1] = rgb.g * 255;
      pixels.data[i + 2] = rgb.b * 255;
      pixels.data[i + 3] = 255;
    }
  }

  // keep the nearest surface visible at each pixel
  scene.traverse((object) => {
    if (!(object instanceof T.Mesh)) return;
    for (let parent: T.Object3D | null = object; parent; parent = parent.parent) if (!parent.visible) return;
    const material = object.material;
    if (!(material instanceof T.MeshStandardMaterial)) return;
    const index = object.geometry.index;
    const count = index ? index.count : object.geometry.attributes.position.count;
    for (let i = 0; i < count; i += 3) {
      const points = [0, 1, 2].map((j) =>
        object.getVertexPosition(index ? index.getX(i + j) : i + j, new T.Vector3()).applyMatrix4(object.matrixWorld),
      );
      const normal = points[1].clone().sub(points[0]).cross(points[2].clone().sub(points[0])).normalize();
      const center = points[0]
        .clone()
        .add(points[1])
        .add(points[2])
        .multiplyScalar(1 / 3);
      if (material.side !== T.DoubleSide && normal.dot(cameraPosition.clone().sub(center)) <= 0) continue;
      material.color
        .clone()
        .multiplyScalar(0.55 + 0.45 * Math.max(0, normal.dot(sun)))
        .getRGB(rgb, T.SRGBColorSpace);
      const red = rgb.r * 255,
        green = rgb.g * 255,
        blue = rgb.b * 255;
      points.forEach((point) => {
        point.project(camera);
        point.x = ((point.x + 1) * canvas.width) / 2;
        point.y = ((1 - point.y) * canvas.height) / 2;
      });
      if (points.every((point) => point.z < -1 || point.z > 1)) continue;
      const [a, b, c] = points;
      const area = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y);
      if (Math.abs(area) < 0.00001) continue;
      const left = Math.max(0, Math.floor(Math.min(a.x, b.x, c.x)));
      const right = Math.min(canvas.width - 1, Math.ceil(Math.max(a.x, b.x, c.x)));
      const top = Math.max(0, Math.floor(Math.min(a.y, b.y, c.y)));
      const bottom = Math.min(canvas.height - 1, Math.ceil(Math.max(a.y, b.y, c.y)));
      for (let y = top; y <= bottom; y++)
        for (let x = left; x <= right; x++) {
          const u = ((b.y - c.y) * (x + 0.5 - c.x) + (c.x - b.x) * (y + 0.5 - c.y)) / area;
          const v = ((c.y - a.y) * (x + 0.5 - c.x) + (a.x - c.x) * (y + 0.5 - c.y)) / area;
          const w = 1 - u - v;
          if (u < -0.000001 || v < -0.000001 || w < -0.000001) continue;
          const z = u * a.z + v * b.z + w * c.z,
            offset = y * canvas.width + x;
          if (z < -1 || z > 1 || z >= depth[offset]) continue;
          depth[offset] = z;
          const pixel = offset * 4;
          pixels.data[pixel] = red;
          pixels.data[pixel + 1] = green;
          pixels.data[pixel + 2] = blue;
          pixels.data[pixel + 3] = 255;
        }
    }
  });
  context.putImageData(pixels, 0, 0);
}
